import React, { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Button, message, Grid, Typography } from 'antd';
// import IndependentChat from "../components/uchat/UCIndependentChat";
import { traceApi, fetchSetting, uchatApi } from '../services/api';
import { useIntl } from 'react-intl';
import { LoadingOutlined, LeftOutlined, BulbOutlined, MoonOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useRef } from 'react';
import CategoryReplyPreview from '../components/uchat/UCCategoryPrevew';
import UCQuestionPage from '../components/uchat/UCQuestionPage';
import type { Category, CategoryConfig, CategoryQuestionNode } from '../types/category';
import { hasQuestionTree, isQuestionBranch } from '../types/categoryQuestionTree';
import { createUChatPageStyles } from '../styles/uchat/index';
import IndependentChat from '../components/uchat/UCIndependentChat/Chat';
import { useTheme } from '../components/uchat/UCIndependentChat/hooks/useTheme';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const ChatPage = () => {
  const screens = useBreakpoint();
  const location = useLocation();
  const { colors, toggleTheme, isDark } = useTheme();
  const [params, setParams] = useState<{
    csUuid?: string; // 聊天会话ID
    code: string;
    category?: string; // 分类信息，现在支持动态分类名称
    status?: string; // 聊天状态
  }>();
  const [loading, setLoading] = useState(false); // 加载状态
  const [formSubmitted, setFormSubmitted] = useState(false); // 表单提交状态

  const intl = useIntl(); // 国际化
  const [code, setCode] = useState(''); // URL中的code参数
  const [uid, setUid] = useState(''); // URL中的uid参数
  const [username, setUsername] = useState(''); // URL中的username参数
  const [hasInitialized, setHasInitialized] = useState(false); // 防止重复初始化
  const [showCategorySelector, setShowCategorySelector] = useState(false); // 是否显示分类选择器，默认关闭
  const [categoryData, setCategoryData] = useState<Category | undefined>(undefined); // 从接口获取的分类配置数据
  const [categoryLoaded, setCategoryLoaded] = useState(false); // 分类配置是否已加载完成
  const [selectedCategoryConfig, setSelectedCategoryConfig] = useState<CategoryConfig | undefined>(undefined); // 选中的分类配置
  const [showQuestionSelector, setShowQuestionSelector] = useState(false);
  const [questionStack, setQuestionStack] = useState<CategoryQuestionNode[][]>([]);
  const chatRef = useRef<{ fetchChatContent: () => Promise<void> }>();
  const handleChatRef = useCallback((ref: { fetchChatContent: () => Promise<void> }) => {
    chatRef.current = ref;
  }, []);

  // 返回到分类页
  const handleBackToCategory = useCallback(() => {
    // 在清空 params 之前先检查状态，如果状态为 close，不触发埋点
    const shouldTriggerTrace = !params || params.status !== 'close';

    setParams(undefined);
    setFormSubmitted(false);
    setShowCategorySelector(false);
    setShowQuestionSelector(false);
    setQuestionStack([]);

    // 如果状态不是 close，触发"进入确认页"埋点
    if (shouldTriggerTrace) {
      // 重置埋点引用，以便重新触发"进入确认页"埋点
      confirmTraceSentRef.current = false;
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        if (code && uid) {
          traceApi.toConfirmPage({ code, uid });
          confirmTraceSentRef.current = true;
        }
      } catch (e) {
        // ignore trace error
      }
    }
  }, [uid, location.search, params]);

  // 解析分类配置（字符串或对象）
  const parseCategoryData = useCallback((rawCategory: unknown) => {
    if (!rawCategory) return undefined;
    if (typeof rawCategory === 'string') {
      try {
        return JSON.parse(rawCategory);
      } catch (parseError) {
        console.error('解析分类配置JSON失败:', parseError);
        return undefined;
      }
    }
    return rawCategory as Category;
  }, []);

  // 构建包含所有必需字段的URL（category 可选，无分类时不写入）
  const buildChatUrl = useCallback(
    (params: { csUuid?: string; code: string; uid: string; username: string; category?: string }) => {
      const searchParams = new URLSearchParams();
      if (params.csUuid) {
        searchParams.set('csUuid', params.csUuid);
      }
      searchParams.set('code', params.code);
      searchParams.set('uid', params.uid);
      searchParams.set('username', params.username);
      if (params.category) {
        searchParams.set('category', params.category);
      }
      return `${window.location.pathname}?${searchParams.toString()}`;
    },
    []
  );

  // 不选择分类直接开始聊天
  const handleStartWithoutCategory = useCallback(async () => {
    setSelectedCategoryConfig(undefined);
    setShowCategorySelector(false);
    setLoading(true);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const urlToken = searchParams.get('token');
      const createData: any = {
        code,
        uid,
        username
      };
      if (urlToken) {
        createData.token = urlToken;
      }
      const createResp = await uchatApi.createCs(createData);

      if (createResp?.success === false && createResp?.code === 'C_1') {
        setLoading(false);
        return;
      }

      if (createResp?.success) {
        const data = createResp.data || {};
        const uuid = data.uuid || '';
        const newCode = data.code || code;
        const status = data.status || '';
        const currentCategory = data.category || '';

        if (newCode && newCode !== code) {
          setCode(newCode);
        }

        setParams({
          csUuid: uuid,
          code: newCode,
          category: currentCategory || undefined,
          status
        });

        if (status !== 'close') {
          conversationTraceSentRef.current = false;
          try {
            traceApi.inConversation({ code: newCode, uuid });
            conversationTraceSentRef.current = true;
          } catch (e) {
            // ignore
          }
        }

        if (uuid && newCode) {
          const newUrl = buildChatUrl({
            csUuid: uuid,
            code: newCode,
            uid,
            username,
            ...(currentCategory && { category: currentCategory })
          });
          window.history.replaceState({}, '', newUrl);
        }

        if (newCode !== code) {
          try {
            const settingResponse = await fetchSetting(newCode);
            if (settingResponse?.success && settingResponse?.data?.category) {
              const parsed = parseCategoryData(settingResponse.data.category);
              if (parsed) setCategoryData(parsed);
            }
          } catch (error) {
            console.error('获取分类配置失败:', error);
          }
        }

        setFormSubmitted(true);
      } else {
        message.error(
          createResp?.msg ||
            intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' })
        );
      }
    } catch (error) {
      console.error('初始化失败', error);
      message.error(intl.formatMessage({ id: 'chat.init_failed' }));
    } finally {
      setLoading(false);
    }
  }, [code, uid, username, intl, buildChatUrl, parseCategoryData]);

  // 处理CategoryPreview的分类选择
  const handleCategoryConfigSelect = useCallback(
    async (config: CategoryConfig, questionLeafId?: string, skipQuestionTree?: boolean) => {
      // 切换分类时创建新会话，先重置状态
      setSelectedCategoryConfig(config);
      setShowCategorySelector(false);
      setLoading(true);
      // 重置滚动位置到顶部
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      try {
        if (!questionLeafId) {
          try {
            const chattingResp = await uchatApi.chattingList(code, uid);
            const list = Array.isArray(chattingResp?.data) ? chattingResp.data : [];
            const existing = list.find((cs: any) => cs.category === config.name);
            if (existing?.uuid) {
              const sessionCode = existing.kbUuid || existing.code || code;
              setParams({
                csUuid: existing.uuid,
                code: sessionCode,
                category: config.name as any,
                status: existing.status
              });
              if (existing.status !== 'close') {
                conversationTraceSentRef.current = false;
                try {
                  traceApi.inConversation({ code: sessionCode, uuid: existing.uuid });
                  conversationTraceSentRef.current = true;
                } catch (e) {
                  // ignore trace error
                }
              }
              const newUrl = buildChatUrl({
                csUuid: existing.uuid,
                code: sessionCode,
                uid,
                username,
                category: config.name
              });
              window.history.replaceState({}, '', newUrl);
              setFormSubmitted(true);
              setShowQuestionSelector(false);
              return;
            }
          } catch (error) {
            console.error(error);
          }
          // Other：跳过选题，按无问题树流程直接建会话
          if (!skipQuestionTree && hasQuestionTree(config)) {
            setQuestionStack([config.questionTree || []]);
            setShowQuestionSelector(true);
            return;
          }
        }

        // 从URL中读取token（如果存在）
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');

        // 创建新会话
        const createData: any = {
          code: code,
          uid: uid,
          username: username,
          category: config.name
        };
        // 如果URL中存在token，将其传递给后端接口
        if (urlToken) {
          createData.token = urlToken;
        }
        if (questionLeafId) {
          createData.questionLeafId = questionLeafId;
        }
        const createResp = await uchatApi.createCs(createData);

        if (createResp && createResp.success === false && createResp.code === 'C_1') {
          setLoading(false);
          return;
        }

        if (createResp && createResp.success) {
          const data = createResp.data || {};
          const uuid = data.uuid || '';
          const newCode = data.code || code;
          const status = data.status || '';

          // 更新 code 状态
          if (newCode && newCode !== code) {
            setCode(newCode);
          }

          // 更新所有会话信息
          setParams({
            csUuid: uuid,
            code: newCode,
            category: config.name as any,
            status: status
          });

          // 如果会话状态不是 close，触发"进入会话页"埋点
          if (status !== 'close') {
            // 重置埋点引用，以便重新触发"进入会话页"埋点
            conversationTraceSentRef.current = false;
            // 触发"进入会话页"埋点
            try {
              traceApi.inConversation({ code: newCode, uuid: uuid });
              conversationTraceSentRef.current = true;
            } catch (e) {
              // ignore trace error
            }
          }

          // 更新 URL 包含所有必需字段
          if (uuid && newCode) {
            const newUrl = buildChatUrl({
              csUuid: uuid,
              code: newCode,
              uid: uid,
              username: username,
              category: config.name
            });
            window.history.replaceState({}, '', newUrl);
          }

          // 如果 code 发生变化，重新获取分类配置数据
          if (newCode && newCode !== code) {
            try {
              const settingResponse = await fetchSetting(newCode);
              if (settingResponse && settingResponse.success && settingResponse.data && settingResponse.data.category) {
                const parsed = parseCategoryData(settingResponse.data.category);
                if (parsed) {
                  setCategoryData(parsed);
                  // 更新选中的分类配置
                  const currentConfig = parsed.configs?.find((cfg: CategoryConfig) => cfg.name === config.name);
                  if (currentConfig) {
                    setSelectedCategoryConfig(currentConfig);
                  }
                }
              }
            } catch (error) {
              console.error('获取分类配置失败:', error);
            }
          }

          setShowQuestionSelector(false);
          setQuestionStack([]);
          setFormSubmitted(true);
        } else {
          message.error(intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' }));
        }
      } catch (error) {
        console.error('初始化失败', error);
        message.error(intl.formatMessage({ id: 'chat.init_failed' }));
      } finally {
        setLoading(false);
      }
    },
    [code, uid, intl, username, buildChatUrl, parseCategoryData]
  );

  const handleQuestionSelect = useCallback(
    (node: CategoryQuestionNode) => {
      if (isQuestionBranch(node)) {
        setQuestionStack(prev => [...prev, node.children || []]);
        return;
      }
      if (selectedCategoryConfig) {
        handleCategoryConfigSelect(selectedCategoryConfig, node.id);
      }
    },
    [selectedCategoryConfig, handleCategoryConfigSelect]
  );

  const handleQuestionOther = useCallback(() => {
    if (selectedCategoryConfig) {
      handleCategoryConfigSelect(selectedCategoryConfig, undefined, true);
    }
  }, [selectedCategoryConfig, handleCategoryConfigSelect]);

  const handleQuestionBack = useCallback(() => {
    setQuestionStack(prev => {
      if (prev.length <= 1) {
        setShowQuestionSelector(false);
        return [];
      }
      return prev.slice(0, -1);
    });
  }, []);

  // 获取分类显示名称
  const getCategoryDisplayName = useCallback((category: string) => {
    if (typeof category === 'string') return category;
    return '';
  }, []);

  // 埋点：进入确认页（仅触发一次）
  const confirmTraceSentRef = useRef(false);
  useEffect(() => {
    // 检查URL中是否有csUuid，如果有说明已经在会话中，不应该触发"进入确认页"埋点
    const searchParams = new URLSearchParams(location.search);
    const urlCsUuid = searchParams.get('csUuid');
    if (urlCsUuid) {
      // URL中有csUuid，说明已经在会话中，不触发"进入确认页"埋点
      return;
    }
    // 如果当前会话状态为 close，不触发"进入确认页"埋点
    if (params && params.status === 'close') {
      return;
    }
    const inConfirmPage = showCategorySelector || (!params && !formSubmitted && uid);
    if (inConfirmPage && !confirmTraceSentRef.current) {
      confirmTraceSentRef.current = true;
      try {
        const code = searchParams.get('code');
        traceApi.toConfirmPage({ code, uid });
      } catch (e) {
        // ignore trace error
      }
    }
  }, [showCategorySelector, params, formSubmitted, uid, code, location.search]);

  // 埋点：进入会话页（仅触发一次）
  const conversationTraceSentRef = useRef(false);
  useEffect(() => {
    // 如果会话状态为 close，不触发埋点
    if (params && params.status === 'close') {
      return;
    }
    if (params && !conversationTraceSentRef.current) {
      conversationTraceSentRef.current = true;
      try {
        traceApi.inConversation({ code, uuid: params.csUuid });
      } catch (e) {
        // ignore trace error
      }
      // 进入聊天页面时重置滚动位置到顶部
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [params, code]);

  // 获取设置数据
  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!code) return;
      setCategoryLoaded(false);
      setCategoryData(undefined);
      try {
        const response = await fetchSetting(code);
        if (response?.success && response.data?.category) {
          const parsed = parseCategoryData(response.data.category);
          if (parsed) setCategoryData(parsed);
        }
      } catch (error) {
        console.error('获取分类配置失败:', error);
      } finally {
        setCategoryLoaded(true);
      }
    };
    fetchCategoryData();
  }, [code, parseCategoryData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCode = searchParams.get('code');
    const urlUid = searchParams.get('uid');
    const urlUsername = searchParams.get('username');
    const urlCsUuid = searchParams.get('csUuid');

    setCode(urlCode || '');

    // 首先处理uid和username的设置
    let currentUid = urlUid || '';
    let currentUsername = urlUsername || '';

    if (!currentUid && !currentUsername) {
      // 如果没有uid和用户名，自动生成游客身份
      const guestId = `Guest-${Math.floor(Math.random() * 100000)}`;
      currentUid = guestId;
      currentUsername = guestId;
      setUid(guestId);
      setUsername(guestId);
    } else {
      setUid(currentUid);
      setUsername(currentUsername || currentUid);
    }

    // 如果有 csUuid，查询现有会话
    if (urlCsUuid && !hasInitialized) {
      setLoading(true);
      uchatApi
        .queryCs(urlCsUuid)
        .then(response => {
          if (response && response.success === false && response.code === 'C_1') {
            // 中断后续链路
            throw new Error('CHAT_CLOSED');
          }

          if (response && response.success) {
            const currentCategory = response.data.category;
            const sessionCode = response.data.kbUuid;
            const status = response.data.status;
            // 优先使用响应中的数据，其次使用URL参数，最后使用当前设置的值
            const finalUid = response.data.uid || currentUid;
            const finalUsername = response.data.username || currentUsername;

            setParams({
              csUuid: urlCsUuid,
              code: sessionCode,
              category: currentCategory,
              status: status
            });

            setCode(sessionCode);
            // 更新uid和username状态
            setUid(finalUid);
            setUsername(finalUsername);

            // 更新URL包含所有必需字段
            if (sessionCode && finalUid && finalUsername) {
              const newUrl = buildChatUrl({
                csUuid: urlCsUuid,
                code: sessionCode,
                uid: finalUid,
                username: finalUsername,
                ...(currentCategory && { category: currentCategory })
              });
              window.history.replaceState({}, '', newUrl);
            }

            return fetchSetting(sessionCode).then(settingResponse => {
              if (settingResponse && settingResponse.success && settingResponse.data && settingResponse.data.category) {
                let categoryData;
                if (typeof settingResponse.data.category === 'string') {
                  try {
                    categoryData = JSON.parse(settingResponse.data.category);
                  } catch (parseError) {
                    console.error('解析分类配置JSON失败:', parseError);
                  }
                } else {
                  categoryData = settingResponse.data.category;
                }
                setCategoryData(categoryData);

                if (currentCategory && categoryData?.configs) {
                  const currentConfig = categoryData.configs.find(
                    (config: CategoryConfig) => config.name === currentCategory
                  );
                  if (currentConfig) {
                    setSelectedCategoryConfig(currentConfig);
                  }
                }
              }
            });
          } else {
            message.error(intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' }));
          }
        })
        .catch(error => {
          if (error && error.message === 'CHAT_CLOSED') return;
          console.error('初始化失败', error);
          message.error(intl.formatMessage({ id: 'chat.init_failed' }));
        })
        .finally(() => {
          setLoading(false);
          setHasInitialized(true);
        });
    } else if (!urlCsUuid && urlCode && currentUid && !hasInitialized) {
      // 如果没有 csUuid，但有 code 和 uid，先尝试调用 createCs 获取最新会话
      setLoading(true);
      const urlToken = searchParams.get('token');

      const createData: any = {
        code: urlCode,
        uid: currentUid,
        username: currentUsername || currentUid
      };
      // 如果URL中存在token，将其传递给后端接口
      if (urlToken) {
        createData.token = urlToken;
      }

      uchatApi
        .createCs(createData)
        .then(response => {
          if (response && response.success === false && response.code === 'C_1') {
            // 聊天已关闭，显示分类选择器
            setLoading(false);
            setHasInitialized(true);
            return;
          }

          if (response && response.success && response.data) {
            const data = response.data || {};
            const uuid = data.uuid || '';
            const sessionCode = data.code || urlCode;
            const currentCategory = data.category || '';
            const status = data.status || '';

            // 如果返回了会话 uuid，说明有最新会话，直接使用（支持有分类或无分类）
            if (uuid) {
              // 更新 code 状态
              if (sessionCode && sessionCode !== urlCode) {
                setCode(sessionCode);
              } else {
                setCode(urlCode);
              }

              // 更新所有会话信息
              setParams({
                csUuid: uuid,
                code: sessionCode || urlCode,
                category: currentCategory,
                status: status
              });

              // 更新 URL 包含所有必需字段
              if (uuid && sessionCode) {
                const newUrl = buildChatUrl({
                  csUuid: uuid,
                  code: sessionCode,
                  uid: currentUid,
                  username: currentUsername || currentUid,
                  ...(currentCategory && { category: currentCategory })
                });
                window.history.replaceState({}, '', newUrl);
              }

              // 获取分类配置数据
              return fetchSetting(sessionCode || urlCode)
                .then(settingResponse => {
                  if (
                    settingResponse &&
                    settingResponse.success &&
                    settingResponse.data &&
                    settingResponse.data.category
                  ) {
                    const parsed = parseCategoryData(settingResponse.data.category);
                    if (parsed) {
                      setCategoryData(parsed);
                      // 更新选中的分类配置
                      if (currentCategory && parsed.configs) {
                        const currentConfig = parsed.configs.find(
                          (cfg: CategoryConfig) => cfg.name === currentCategory
                        );
                        if (currentConfig) {
                          setSelectedCategoryConfig(currentConfig);
                        }
                      }
                    }
                  }
                })
                .catch(error => {
                  console.error('获取分类配置失败:', error);
                })
                .finally(() => {
                  setLoading(false);
                  setHasInitialized(true);
                });
            } else {
              // 没有返回会话或分类，显示分类选择器
              setLoading(false);
              setHasInitialized(true);
            }
          } else {
            // 创建失败或无数据，显示分类选择器
            setLoading(false);
            setHasInitialized(true);
          }
        })
        .catch(error => {
          console.error('获取最新会话失败', error);
          // 出错时显示分类选择器
          setLoading(false);
          setHasInitialized(true);
        });
    } else if (!hasInitialized) {
      // 既没有 csUuid，也没有 code 和 uid，直接完成初始化
      setHasInitialized(true);
    }
  }, [location.search, hasInitialized, intl, parseCategoryData, buildChatUrl]);

  // 使用抽离的样式，避免重复计算
  const styles = useMemo(() => createUChatPageStyles(screens, colors), [screens, colors]);

  return (
    <div style={styles.container}>
      {!params && !formSubmitted && uid && !showQuestionSelector ? (
        <div style={styles.mainContainer}>
          <div style={styles.contentWrapper}>
            {/* 分类选择器（包含欢迎信息） */}
            <CategoryReplyPreview
              categoryData={categoryData}
              onCategorySelect={handleCategoryConfigSelect}
              onStartWithoutCategory={
                categoryLoaded && !(categoryData?.configs && categoryData.configs.length > 0)
                  ? handleStartWithoutCategory
                  : undefined
              }
              selectedCategory={selectedCategoryConfig}
              userName={username}
              themeColors={colors}
              toggleTheme={toggleTheme}
              isDark={isDark}
            />
          </div>
        </div>
      ) : null}

      {!params && !formSubmitted && uid && showQuestionSelector && selectedCategoryConfig ? (
        <UCQuestionPage
          categoryConfig={selectedCategoryConfig}
          options={questionStack[questionStack.length - 1] || []}
          colors={{
            background: colors.background,
            text: colors.text,
            cardBg: colors.messageBg
          }}
          onBack={handleQuestionBack}
          onSelect={handleQuestionSelect}
          onSelectOther={handleQuestionOther}
        />
      ) : null}

      {loading && (
        <div style={styles.loadingOverlay}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
            tip={intl.formatMessage({ id: 'chat.initializing' })}
          />
        </div>
      )}

      {params && (
        <div style={styles.chatContainer}>
          <div style={styles.chatWrapper}>
            {/* 聊天头部 - 显示返回按钮、分类信息（有分类时）、主题切换 */}
            <div style={styles.chatHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  type="text"
                  size="large"
                  icon={<LeftOutlined style={{ fontSize: 20, color: colors.text }} />}
                  onClick={handleBackToCategory}
                  style={{ marginRight: 8, width: 40, height: 40 }}
                />
              </div>
              <div style={styles.headerContent}>
                {params.category && categoryData?.configs ? (
                  <div
                    style={{ ...styles.categoryInfo, cursor: 'pointer' }}
                    onClick={() => setShowCategorySelector(v => !v)}
                  >
                    <Text style={{ ...styles.categoryLabel, fontSize: 16 }}>
                      {intl.formatMessage({ id: 'consultation.type' })}
                    </Text>
                    <Text
                      style={{
                        ...styles.categoryName,
                        fontSize: 20,
                        fontWeight: 600
                      }}
                    >
                      {getCategoryDisplayName(params.category)}
                    </Text>
                  </div>
                ) : (
                  <div style={styles.categoryInfo}>
                    <Text
                      style={{
                        ...styles.categoryName,
                        fontSize: 20,
                        fontWeight: 600
                      }}
                    >
                      {intl.formatMessage({
                        id: 'chat.direct_chat.label',
                        defaultMessage: '直接咨询'
                      })}
                    </Text>
                  </div>
                )}
              </div>
              {/* 主题切换按钮 */}
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  type="text"
                  size="large"
                  icon={
                    isDark ? (
                      <BulbOutlined style={{ fontSize: 20, color: colors.text }} />
                    ) : (
                      <MoonOutlined style={{ fontSize: 20, color: colors.text }} />
                    )
                  }
                  onClick={toggleTheme}
                  style={{ width: 40, height: 40 }}
                />
              </div>
            </div>

            {/* 聊天组件 */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              {/* 内联分类切换，仅名称按钮，贴在标题下方 */}
              {showCategorySelector && params && categoryData?.configs && (
                <div style={styles.categorySelectorContainer}>
                  <div style={styles.categorySelectorGrid}>
                    {(categoryData.configs || []).map((cfg: CategoryConfig, idx: number) => {
                      const isSelected = (selectedCategoryConfig?.name || params.category) === cfg.name;
                      return (
                        <Button
                          key={idx}
                          type={isSelected ? 'primary' : 'default'}
                          onClick={() => handleCategoryConfigSelect(cfg)}
                          style={{
                            ...styles.categoryButton,
                            ...(isSelected ? {} : styles.categoryButtonUnselected)
                          }}
                        >
                          {cfg.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
              <IndependentChat
                key={params.csUuid} // 使用 csUuid 作为 key，确保切换会话时完全重新加载
                csUuid={params.csUuid}
                code={params.code}
                uid={uid}
                onRef={handleChatRef}
                status={params.status}
                themeColors={colors}
                isDark={isDark}
                quickQaIds={
                  selectedCategoryConfig?.quickQa && selectedCategoryConfig.quickQa.length > 0
                    ? selectedCategoryConfig.quickQa
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
